import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import { cbtAPI } from '../../api/cbt';
import MyTextEditor from './MyTextEditor';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const CbtQuestion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get parameters
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id');
  const cbtDataId = searchParams.get('cbt_data_id');
  
  // State
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [testSession, setTestSession] = useState(null);
  const [cbtData, setCbtData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [expandedImage, setExpandedImage] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const debouncedSaveRef = useRef(null);
  const initialFetchDoneRef = useRef(false);
  const activityTimerRef = useRef(null);

  // Helper functions
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  const formatTime = useCallback((seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const getTimeDisplayColor = useCallback(() => {
    if (timeLeft > 600) return 'text-green-600';
    if (timeLeft > 300) return 'text-yellow-600';
    return 'text-red-600 animate-pulse';
  }, [timeLeft]);

  const getQuestionStatus = useCallback((questionId) => {
    if (answers[questionId]) return 'answered';
    if (flaggedQuestions.has(questionId)) return 'flagged';
    return 'unanswered';
  }, [answers, flaggedQuestions]);

  // FIXED: getAnsweredCount is a value, not a function
  const getAnsweredCount = useMemo(() => {
    return Object.keys(answers).length;
  }, [answers]);

  const toggleFlagQuestion = useCallback((questionId) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
        toast.info('Question unflagged');
      } else {
        newSet.add(questionId);
        toast.info('Question flagged for review');
      }
      return newSet;
    });
  }, []);

  // ✅ Transform server answer format to frontend format
  const transformServerAnswerToFrontend = useCallback((question, studentAnswer) => {
    if (!studentAnswer) return null;
    
    const { question_type } = question;
    
    switch (question_type) {
      case 'multiple_choice':
        return {
          multiple_choices: studentAnswer.multiple_choices || [],
          answer_type: 'multiple_choice',
          timestamp: Date.now()
        };
      case 'single_choice':
        return {
          selected_option_id: studentAnswer.selected_option_id || 
                             (studentAnswer.multiple_choices && studentAnswer.multiple_choices[0]),
          answer_type: 'single_choice',
          timestamp: Date.now()
        };
      case 'true_false':
        return {
          true_false_answer: studentAnswer.true_false_answer,
          answer_type: 'true_false',
          timestamp: Date.now()
        };
      case 'essay':
      case 'theory':
        return {
          answer_text: studentAnswer.answer_text,
          answer_type: question_type === 'essay' ? 'essay' : 'theory',
          timestamp: Date.now()
        };
      case 'fill_blank':
        return {
          answer_text: studentAnswer.answer_text,
          answer_type: 'fill_blank',
          timestamp: Date.now()
        };
      default:
        return null;
    }
  }, []);

  const saveAnswerToServer = useCallback(async (questionId, answerData) => {
    if (!sessionId || !questionId) return;
    
    try {
      // Send in the correct format that backend expects
      const payload = {
        question_id: questionId,
        ...answerData
      };
      
      await cbtAPI.submitAnswer(sessionId, questionId, payload);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to autosave answer:', error);
      
      // Show error message to user
      if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || 'Invalid selection');
      } else if (error.response?.status === 404) {
        toast.error('Question not found');
      } else {
        toast.error('Failed to save answer');
        
        // Store locally as fallback
        const backup = {
          sessionId,
          questionId,
          answer: answerData,
          timestamp: Date.now()
        };
        localStorage.setItem(`cbt_backup_${sessionId}_${questionId}`, JSON.stringify(backup));
      }
    }
  }, [sessionId]);

  // ✅ NEW: Submit all answers before final submission
  const submitAllAnswers = useCallback(async () => {
    if (!sessionId || Object.keys(answers).length === 0) return true;
    
    try {
      // Submit each answer individually
      for (const [questionId, answerData] of Object.entries(answers)) {
        const payload = {
          question_id: questionId,
          ...answerData,
          // Remove timestamp for final submission
          timestamp: undefined
        };
        
        await cbtAPI.submitAnswer(sessionId, questionId, payload);
      }
      
      // Clear backups after successful submission
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(`cbt_backup_${sessionId}`)) {
          localStorage.removeItem(key);
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error submitting answers:', error);
      toast.error('Failed to submit some answers. Please try again.');
      return false;
    }
  }, [sessionId, answers]);

  // ✅ UPDATED: Submit test with all answers
  const submitTest = useCallback(async () => {
    if (!sessionId) {
      toast.error('No active test session');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // First submit all pending answers
      const allAnswersSubmitted = await submitAllAnswers();
      if (!allAnswersSubmitted) {
        toast.error('Failed to submit all answers. Please try again.');
        setSubmitting(false);
        return;
      }
      
      // Then submit the test
      await cbtAPI.submitTest(sessionId);
      
      // Get results
      const resultsResponse = await cbtAPI.getTestResults(sessionId);
      
      // Navigate to completion page
      navigate('/participate/test_completion', {
        state: {
          sessionId,
          results: resultsResponse.data
        }
      });
      
    } catch (error) {
      console.error('Submit test error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit test');
    } finally {
      setSubmitting(false);
      setShowSubmitModal(false);
    }
  }, [sessionId, answers, submitAllAnswers, navigate]);

  // Handle auto-submit on timeout
  const handleAutoSubmit = useCallback(async () => {
    if (submitting || testSession?.status !== 'in_progress') return;
    
    try {
      setSubmitting(true);
      toast.info("Time's up! Auto-submitting your test...");
      await submitTest();
    } catch (error) {
      toast.error('Failed to auto-submit test');
      console.error('Auto-submit error:', error);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, testSession, submitTest]);

  // ✅ UPDATED: Initialize exam with server data including previous answers
  const initializeExam = useCallback(async () => {
    if (initialFetchDoneRef.current || !sessionId) return;
    
    try {
      setLoading(true);
      initialFetchDoneRef.current = true;
      
      // 1. Fetch session
      const sessionResponse = await cbtAPI.getTestSession(sessionId);
      const session = sessionResponse.data;
      
      if (session.status !== 'in_progress') {
        toast.error('Test session is not active');
        navigate('/participate/dashboard');
        return;
      }
      
      setTestSession(session);
      
      // 2. Fetch CBT details AND questions IN PARALLEL
      const [cbtResponse, questionsResponse] = await Promise.all([
        cbtAPI.getCBTDetails(session.cbt_data_id),
        // ✅ Pass session_id to get previous answers
        cbtAPI.getCBTQuestions(session.cbt_data_id, sessionId)
      ]);
      
      const cbt = cbtResponse.data;
      setCbtData(cbt);
      
      // 3. Process questions
      let questionsList = questionsResponse.data.questions || [];
      
      if (cbt.randomize_questions) {
        questionsList = shuffleArray(questionsList);
      }
      
      if (cbt.max_questions > 0 && cbt.max_questions < questionsList.length) {
        questionsList = questionsList.slice(0, cbt.max_questions);
      }
      
      setQuestions(questionsList);
      
      // ✅ 4. Load saved answers from server response
      const initialAnswers = {};
      questionsList.forEach(question => {
        if (question.student_answer) {
          const transformedAnswer = transformServerAnswerToFrontend(question, question.student_answer);
          if (transformedAnswer) {
            initialAnswers[question.cbt_question_id] = transformedAnswer;
          }
        }
      });
      
      setAnswers(initialAnswers);
      
      // 5. Set timer
      const timeRemaining = session.time_remaining || cbt.cbt_duration * 60;
      setTimeLeft(timeRemaining);
      
      // 6. Check for local backup answers
      const backupAnswers = {};
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(`cbt_backup_${sessionId}`)) {
          try {
            const backup = JSON.parse(localStorage.getItem(key));
            if (backup && backup.questionId && backup.answer) {
              backupAnswers[backup.questionId] = backup.answer;
            }
          } catch (e) {
            console.error('Error parsing backup:', e);
          }
        }
      });
      
      // Merge backup answers (prefer server answers over backups)
      if (Object.keys(backupAnswers).length > 0) {
        setAnswers(prev => ({
          ...backupAnswers,
          ...prev // Server answers override backups
        }));
      }
      
    } catch (error) {
      console.error('Initialization error:', error);
      toast.error(error.response?.data?.detail || 'Failed to load exam');
      navigate('/participate/dashboard');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [sessionId, navigate, shuffleArray, transformServerAnswerToFrontend]);

  // Handle answer change - LOCAL FIRST
  const handleAnswerChange = useCallback((questionId, answerData) => {
    // Validate max_options_select on the frontend first
    const question = questions.find(q => q.cbt_question_id === questionId);
    
    if (question && question.question_type === 'multiple_choice') {
      const maxAllowed = question.max_options_select || 1;
      const currentSelections = answers[questionId]?.multiple_choices || [];
      const newSelections = answerData.multiple_choices || [];
      
      // Check if user is trying to select more than allowed
      if (newSelections.length > maxAllowed) {
        toast.error(`You can only select up to ${maxAllowed} option(s).`);
        return; // Don't update state or save
      }
    }
    
    // Update local state immediately
    setAnswers(prev => {
      const newAnswer = {
        ...answerData,
        timestamp: Date.now()
      };
      
      const newAnswers = {
        ...prev,
        [questionId]: newAnswer
      };
      
      // Trigger debounced save - Send in correct format
      if (debouncedSaveRef.current) {
        // Send in the format the backend expects
        debouncedSaveRef.current(questionId, {
          ...answerData,
          timestamp: Date.now()
        });
      }
      
      return newAnswers;
    });
  }, [questions, answers]);

  // Start timer
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [handleAutoSubmit]);

  // Initialize on mount - SINGLE FETCH
  useEffect(() => {
    mountedRef.current = true;
    
    // Initialize debounced save function
    debouncedSaveRef.current = debounce((questionId, answerData) => {
      if (!sessionId || !mountedRef.current) return;
      saveAnswerToServer(questionId, answerData);
    }, 2000);
    
    // Fetch everything once
    initializeExam();
    
    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
        activityTimerRef.current = null;
      }
      
      // Save any unsaved answers before unmounting
      if (debouncedSaveRef.current?.flush) {
        debouncedSaveRef.current.flush();
      }
    };
  }, [initializeExam, sessionId, saveAnswerToServer]);

  // Start timer when timeLeft is set
  useEffect(() => {
    if (timeLeft > 0 && !timerRef.current) {
      startTimer();
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [timeLeft, startTimer]);

  // Event listeners for security
  useEffect(() => {
    if (!testSession || !cbtData || testSession.status !== 'in_progress') return;
    
    const preventActions = (e) => {
      if (!cbtData.allow_copy_paste && e.ctrlKey && ['c', 'v', 'x'].includes(e.key)) {
        e.preventDefault();
        toast.warning('Copy/paste is disabled');
      }
      
      if (e.type === 'contextmenu') {
        e.preventDefault();
        return false;
      }
    };
    
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave? Your test progress may be lost.';
      return e.returnValue;
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        toast.warning('Tab switching may be monitored!');
      }
    };
    
    window.addEventListener('keydown', preventActions);
    window.addEventListener('contextmenu', preventActions);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('keydown', preventActions);
      window.removeEventListener('contextmenu', preventActions);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [testSession, cbtData]);

  // Check for inactivity
  useEffect(() => {
    if (!cbtData?.max_inactivity_time || !testSession) return;
    
    const checkInactivity = () => {
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
      
      activityTimerRef.current = setTimeout(() => {
        toast.warning('Inactivity detected. Please continue with your test.');
      }, cbtData.max_inactivity_time * 60 * 1000);
    };
    
    const resetActivityTimer = () => {
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
      checkInactivity();
    };
    
    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, resetActivityTimer);
    });
    
    // Start initial timer
    checkInactivity();
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetActivityTimer);
      });
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
    };
  }, [cbtData, testSession]);

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Loading Your Test</h3>
          <p className="text-gray-600">Please wait while we prepare your questions...</p>
        </div>
      </div>
    );
  }

  // If no questions, show error
  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <i className="fas fa-exclamation-triangle text-red-500 text-5xl mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Questions Found</h2>
          <p className="text-gray-600 mb-6">The test doesn't contain any questions.</p>
          <button
            onClick={() => navigate('/participate/dashboard')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-600 text-white p-2 rounded-lg">
                <i className="fas fa-graduation-cap text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 truncate max-w-xs">
                  {cbtData?.cbt_name}
                </h1>
                <p className="text-sm text-gray-600">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              {/* Timer */}
              <div className="text-center">
                <div className={`text-2xl font-mono font-bold ${getTimeDisplayColor()}`}>
                  {formatTime(timeLeft)}
                </div>
                <p className="text-xs text-gray-600">Time Remaining</p>
              </div>
              
              {/* Progress */}
              <div className="hidden md:block">
                <div className="text-sm text-gray-600 mb-1">
                  Progress: {Math.round((getAnsweredCount / questions.length) * 100)}%
                </div>
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(getAnsweredCount / questions.length) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Last Saved */}
              {lastSaved && (
                <div className="hidden md:block text-xs text-gray-500">
                  <i className="fas fa-save mr-1"></i>
                  Saved {Math.floor((Date.now() - lastSaved) / 1000 / 60)}m ago
                </div>
              )}
              
              {/* Submit Button */}
              <button
                onClick={() => setShowSubmitModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Submit Test
              </button>
              
              {/* Mobile Menu */}
              <button
                onClick={() => setShowMobileSidebar(true)}
                className="md:hidden p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                <i className="fas fa-bars text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className={`lg:col-span-1 ${showMobileSidebar ? 'block fixed inset-0 bg-white z-50 p-6 overflow-y-auto' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-2xl shadow-lg p-6 h-full">
              {/* Mobile Header */}
              {showMobileSidebar && (
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Navigation</h3>
                  <button
                    onClick={() => setShowMobileSidebar(false)}
                    className="p-2 rounded-lg bg-gray-100"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
              
              {/* Question Grid */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-4">Questions</h4>
                <div className="grid grid-cols-5 gap-2">
                  {questions.map((q, index) => {
                    const status = getQuestionStatus(q.cbt_question_id);
                    const isCurrent = currentQuestionIndex === index;
                    
                    return (
                      <button
                        key={q.cbt_question_id}
                        onClick={() => {
                          setCurrentQuestionIndex(index);
                          setShowMobileSidebar(false);
                        }}
                        className={`h-10 rounded-lg text-sm font-medium transition-all relative ${
                          isCurrent 
                            ? 'bg-blue-600 text-white shadow-lg' 
                            : status === 'answered'
                            ? 'bg-green-100 text-green-800'
                            : status === 'flagged'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {index + 1}
                        {status === 'flagged' && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-500 rounded-full"></span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="text-2xl font-bold text-green-700">{getAnsweredCount}</div>
                  <div className="text-sm text-green-600">Answered</div>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-700">{flaggedQuestions.size}</div>
                  <div className="text-sm text-yellow-600">Flagged</div>
                </div>
              </div>
              
              {/* Settings */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Navigation</span>
                  <span className={`font-semibold ${cbtData?.allow_back_navigation ? 'text-green-600' : 'text-red-600'}`}>
                    {cbtData?.allow_back_navigation ? 'Allowed' : 'Disabled'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Copy/Paste</span>
                  <span className={`font-semibold ${cbtData?.allow_copy_paste ? 'text-green-600' : 'text-red-600'}`}>
                    {cbtData?.allow_copy_paste ? 'Allowed' : 'Disabled'}
                  </span>
                </div>
                {cbtData?.max_inactivity_time && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Inactivity Limit</span>
                    <span className="font-semibold text-orange-600">
                      {cbtData.max_inactivity_time} min
                    </span>
                  </div>
                )}
              </div>
              
              {/* Close mobile sidebar button */}
              {showMobileSidebar && (
                <button
                  onClick={() => setShowMobileSidebar(false)}
                  className="mt-6 w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium"
                >
                  Close Menu
                </button>
              )}
            </div>
          </div>
          
          {/* Question Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {/* Question Header */}
              <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Question {currentQuestionIndex + 1}
                  </h2>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      currentQuestion?.difficulty === 'hard' ? 'bg-red-100 text-red-800' :
                      currentQuestion?.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {currentQuestion?.difficulty || 'Medium'}
                    </span>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {currentQuestion?.question_type || 'Multiple Choice'}
                    </span>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                      {currentQuestion?.score || 1} point(s)
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => toggleFlagQuestion(currentQuestion.cbt_question_id)}
                  className={`p-3 rounded-lg ${
                    flaggedQuestions.has(currentQuestion.cbt_question_id)
                      ? 'bg-yellow-100 text-yellow-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <i className={`fas fa-flag${flaggedQuestions.has(currentQuestion.cbt_question_id) ? '' : '-checkered'}`}></i>
                </button>
              </div>
              
              {/* Question Content */}
              <div className="mb-8">
                <div 
                  className="prose prose-lg max-w-none mb-8 p-4 bg-gray-50 rounded-lg"
                  dangerouslySetInnerHTML={{ __html: currentQuestion.question }}
                />
                
                {/* ✅ Show previous answer indicator */}
                {currentQuestion.student_answer && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center text-blue-700">
                      <i className="fas fa-history mr-2"></i>
                      <span className="font-medium">Previous answer loaded from:</span>
                      <span className="ml-2 text-sm">
                        {new Date(currentQuestion.student_answer.answered_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Render Question Based on Type */}
                {renderQuestionByType(currentQuestion, answers[currentQuestion.cbt_question_id], handleAnswerChange)}
              </div>
              
              {/* Navigation */}
              <div className="flex justify-between pt-6 border-t border-gray-200">
                <button
                  onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                  disabled={!cbtData?.allow_back_navigation || currentQuestionIndex === 0}
                  className={`px-6 py-3 rounded-lg font-medium ${
                    !cbtData?.allow_back_navigation || currentQuestionIndex === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-600 text-white hover:bg-gray-700'
                  }`}
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Previous
                </button>
                
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    {currentQuestionIndex + 1} / {questions.length}
                  </span>
                </div>
                
                <button
                  onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                  disabled={currentQuestionIndex === questions.length - 1}
                  className={`px-6 py-3 rounded-lg font-medium ${
                    currentQuestionIndex === questions.length - 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Next
                  <i className="fas fa-arrow-right ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Warning Banner */}
      {timeLeft > 0 && timeLeft <= 300 && (
        <div className="fixed bottom-4 left-4 right-4 z-50">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-clock text-2xl mr-3"></i>
                <div>
                  <p className="font-bold">Time is running out!</p>
                  <p className="text-sm opacity-90">
                    {timeLeft <= 60 
                      ? 'Less than 1 minute remaining!' 
                      : `${Math.ceil(timeLeft / 60)} minutes remaining`
                    }
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSubmitModal(true)}
                className="px-6 py-2 bg-white text-red-600 rounded-lg font-bold hover:bg-gray-100"
              >
                SUBMIT NOW
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ UPDATED: Submit Modal */}
      <Modal show={showSubmitModal} onHide={() => !submitting && setShowSubmitModal(false)} centered>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title className="text-xl font-bold">Submit Test</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center text-yellow-800">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              <span className="font-semibold">Important Notice</span>
            </div>
            <p className="text-yellow-700 text-sm mt-2">
              You have answered {getAnsweredCount} out of {questions.length} questions.
              {getAnsweredCount < questions.length && (
                <span className="font-bold"> {questions.length - getAnsweredCount} questions remain unanswered.</span>
              )}
            </p>
          </div>
          
          <p className="text-gray-700 mb-6">
            Are you sure you want to submit your test? This action cannot be undone.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setShowSubmitModal(false)}
              disabled={submitting}
              className="py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={submitTest}
              disabled={submitting}
              className={`py-3 rounded-lg font-medium ${submitting ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'} text-white`}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Submitting...
                </>
              ) : 'Submit Test'}
            </button>
          </div>
        </Modal.Body>
      </Modal>
      
      {/* Auto-save indicator */}
      <div className="fixed bottom-4 right-4 text-xs text-gray-500 bg-white px-3 py-1 rounded-full shadow flex items-center">
        <i className="fas fa-save mr-1"></i>
        Auto-save enabled
      </div>
    </div>
  );
};

// ✅ UPDATED: Helper function to render questions by type with previous answers
const renderQuestionByType = (question, currentAnswer, onChange) => {
  const { question_type, answers = [] } = question;
  
  const handleChange = (data) => {
    onChange(question.cbt_question_id, data);
  };
  
  switch (question_type) {
    case 'multiple_choice':
      const maxSelect = question.max_options_select || 1;
      const currentSelections = currentAnswer?.multiple_choices || [];
      const isAtMaxLimit = currentSelections.length >= maxSelect;
      
      return (
        <div className="space-y-4">
          <div className="mb-4 text-sm text-gray-600">
            Select up to {maxSelect} option(s). Currently selected: {currentSelections.length}
          </div>
          
          {answers.map((answer, index) => {
            const isSelected = currentSelections.includes(answer.answer_id);
            const isDisabled = !isSelected && isAtMaxLimit;
            
            return (
              <label
                key={answer.answer_id}
                className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : isDisabled
                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  name={`question_${question.cbt_question_id}`}
                  checked={isSelected}
                  onChange={(e) => {
                    if (isDisabled && e.target.checked) {
                      toast.error(`You can only select up to ${maxSelect} option(s).`);
                      return;
                    }
                    
                    const newChoices = e.target.checked
                      ? [...currentSelections, answer.answer_id]
                      : currentSelections.filter(id => id !== answer.answer_id);
                    
                    handleChange({ 
                      multiple_choices: newChoices, 
                      answer_type: 'multiple_choice' 
                    });
                  }}
                  disabled={isDisabled && !isSelected}
                  className="mt-1 h-5 w-5 text-blue-600"
                />
                <div className="ml-4 flex-1">
                  <div className="font-medium text-gray-900 mb-1">
                    {answer.option_label || String.fromCharCode(65 + index)}.
                  </div>
                  <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: answer.content }} />
                </div>
              </label>
            );
          })}
        </div>
      );
      
    case 'single_choice':
      return (
        <div className="space-y-4">
          {answers.map((answer, index) => (
            <label
              key={answer.answer_id}
              className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${
                currentAnswer?.selected_option_id === answer.answer_id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={`question_${question.cbt_question_id}`}
                checked={currentAnswer?.selected_option_id === answer.answer_id}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleChange({ selected_option_id: answer.answer_id, answer_type: 'single_choice' });
                  }
                }}
                className="mt-1 h-5 w-5 text-blue-600"
              />
              <div className="ml-4 flex-1">
                <div className="font-medium text-gray-900 mb-1">
                  {answer.option_label || String.fromCharCode(65 + index)}.
                </div>
                <div className="text-gray-700" dangerouslySetInnerHTML={{ __html: answer.content }} />
              </div>
            </label>
          ))}
        </div>
      );
      
    case 'true_false':
      return (
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleChange({ true_false_answer: true, answer_type: 'true_false' })}
            className={`p-6 rounded-xl text-center border-2 ${
              currentAnswer?.true_false_answer === true
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-4xl mb-2">✓</div>
            <div className="font-semibold text-lg">True</div>
          </button>
          <button
            onClick={() => handleChange({ true_false_answer: false, answer_type: 'true_false' })}
            className={`p-6 rounded-xl text-center border-2 ${
              currentAnswer?.true_false_answer === false
                ? 'border-red-500 bg-red-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-4xl mb-2">✗</div>
            <div className="font-semibold text-lg">False</div>
          </button>
        </div>
      );
    
    case 'essay':
    case 'theory':
      return (
        <div>
          <MyTextEditor
            initialContent={currentAnswer?.answer_text || ''}
            onChange={(content) => handleChange({ answer_text: content, answer_type: question_type === 'essay' ? 'essay' : 'theory' })}
            height="300px"
            placeholder="Type your answer here..."
          />
          {question.explanation && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-700">
                <i className="fas fa-lightbulb mr-2"></i>
                <span className="font-medium">Tip:</span> {question.explanation}
              </div>
            </div>
          )}
        </div>
      );
    
    case 'fill_blank':
      return (
        <div className="space-y-4">
          {question.question.split('_____').map((part, index, array) => (
            <div key={index} className="inline">
              <span dangerouslySetInnerHTML={{ __html: part }} />
              {index < array.length - 1 && (
                <input
                  type="text"
                  value={currentAnswer?.answer_text || ''}
                  onChange={(e) => handleChange({ answer_text: e.target.value, answer_type: 'fill_blank' })}
                  className="inline-block mx-2 px-3 py-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  placeholder="Type answer here"
                />
              )}
            </div>
          ))}
        </div>
      );
    
    case 'matching':
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Items</h4>
              {answers
                .filter(a => a.option_label === 'item')
                .map((item, index) => (
                  <div key={item.answer_id} className="p-3 bg-gray-50 rounded-lg mb-2">
                    {item.options}
                  </div>
                ))}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Matches</h4>
              {answers
                .filter(a => a.option_label === 'match')
                .map((match, index) => (
                  <div key={match.answer_id} className="p-3 bg-gray-50 rounded-lg mb-2">
                    {match.options}
                  </div>
                ))}
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Drag and drop items to match with corresponding options.
          </p>
        </div>
      );
    
    default:
      return (
        <div className="text-center py-8 text-gray-500">
          <i className="fas fa-question-circle text-4xl mb-4"></i>
          <p>This question type is not yet supported.</p>
        </div>
      );
  }
};

export default CbtQuestion;